/**
 * @file useKitchenOrders.ts
 * @description Hook managing kitchen orders state and real-time subscription synchronization.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../config/supabase.config';
import { fetchActiveKitchenOrders } from '../services/orderQuery.service';
import { updateKitchenOrderStatus, setKitchenOrderPriority } from '../services/orderMutation.service';
import { playNewOrderAlert } from '../services/orderAudio.service';
import type { KitchenOrder, KitchenOrderStatus, KitchenPriority } from '../../../types/order.types';

// Maps Postgres order status values to UI KitchenOrderStatus
const mapDbToKitchenStatus = (status: string | null): KitchenOrderStatus => {
  if (status === 'pending') return 'new';
  if (status === 'preparing') return 'preparing';
  if (status === 'ready') return 'ready';
  if (status === 'completed') return 'done';
  if (status === 'cancelled') return 'cancelled';
  return 'new';
};

export const useKitchenOrders = (venueId: string | null) => {
  const [orders, setOrders] = useState<KitchenOrder[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const previousCountRef = useRef<number>(0);
  const lastFetchedAtRef = useRef<number>(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadOrders = useCallback(async () => {
    if (!venueId) return;
    try {
      setError(null);
      const data = await fetchActiveKitchenOrders(venueId);
      lastFetchedAtRef.current = Date.now();
      
      // Trigger acoustic alert if new orders have arrived
      if (data.length > previousCountRef.current && previousCountRef.current > 0) {
        playNewOrderAlert();
      }
      previousCountRef.current = data.length;
      setOrders(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load kitchen tickets');
    } finally {
      setIsLoading(false);
    }
  }, [venueId]);

  const loadOrdersRef = useRef(loadOrders);
  loadOrdersRef.current = loadOrders;

  // Schedules a debounced reload to coalesce rapid burst events (e.g. multiple items) into a single database query
  const scheduleRefresh = useCallback((immediate?: boolean) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (immediate) {
      loadOrdersRef.current();
      return;
    }
    debounceTimerRef.current = setTimeout(() => {
      loadOrdersRef.current();
    }, 300);
  }, []);

  useEffect(() => {
    if (!venueId) return;
    loadOrdersRef.current();

    // Setup Supabase Realtime channel for order and item mutations with a stable channel identifier
    const channel = supabase
      .channel(`kitchen-orders-live-${venueId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `venue_id=eq.${venueId}`,
        },
        (payload) => {
          // Optimistically update ticket status in state immediately
          if (payload.eventType === 'UPDATE' && payload.new?.id) {
            const updated = payload.new as any;
            setOrders(prev =>
              prev.map(o => {
                if (o.id !== updated.id) return o;
                return {
                  ...o,
                  status: mapDbToKitchenStatus(updated.status),
                  raw_db_status: updated.status ?? o.raw_db_status,
                  priority: (updated.priority as KitchenPriority) ?? o.priority,
                  served_at: updated.served_at ?? o.served_at,
                  confirmed_to_kitchen_at: updated.confirmed_to_kitchen_at ?? o.confirmed_to_kitchen_at,
                  is_confirmed_to_kitchen: Boolean(
                    updated.status === 'confirmed' || updated.confirmed_to_kitchen_at
                  ),
                };
              })
            );
          }
          scheduleRefresh();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'order_items',
        },
        () => {
          scheduleRefresh();
        }
      )
      .subscribe((status) => {
        // Reconnection recovery: catch up missed orders if disconnected
        if (status === 'SUBSCRIBED' && Date.now() - lastFetchedAtRef.current > 30000) {
          scheduleRefresh(true);
        }
      });

    // Reconnection catch-up: refresh only when device reconnects or tab becomes visible after idle (>60s)
    const handleReconnection = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetchedAtRef.current > 60000) {
        scheduleRefresh(true);
      }
    };

    window.addEventListener('visibilitychange', handleReconnection);
    window.addEventListener('online', handleReconnection);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      window.removeEventListener('visibilitychange', handleReconnection);
      window.removeEventListener('online', handleReconnection);
      supabase.removeChannel(channel);
    };
  }, [venueId, scheduleRefresh]);

  const changeStatus = async (orderId: string, nextStatus: KitchenOrderStatus) => {
    // Optimistic UI update for immediate response on touch screens
    setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, status: nextStatus } : o)));
    try {
      await updateKitchenOrderStatus(orderId, nextStatus);
    } catch (err) {
      loadOrders();
      throw err;
    }
  };

  const togglePriority = async (orderId: string, newPriority: KitchenPriority) => {
    setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, priority: newPriority } : o)));
    try {
      await setKitchenOrderPriority(orderId, newPriority);
    } catch (err) {
      loadOrders();
      throw err;
    }
  };

  return {
    orders,
    isLoading,
    error,
    refreshOrders: loadOrders,
    changeStatus,
    togglePriority,
  };
};
